// Pure orientation math. No Three.js dependency, so the constraint solver can be
// unit-tested in Node and the same 24-class vocabulary remains stable for the GNN.
export const ORIENTATION_MATRICES_24 = Object.freeze([
  [1,0,0,0,1,0,0,0,1],[0,-1,0,1,0,0,0,0,1],[-1,0,0,0,-1,0,0,0,1],[0,1,0,-1,0,0,0,0,1],
  [0,0,1,0,1,0,-1,0,0],[0,0,1,1,0,0,0,1,0],[0,0,1,0,-1,0,1,0,0],[0,0,1,-1,0,0,0,-1,0],
  [-1,0,0,0,1,0,0,0,-1],[0,1,0,1,0,0,0,0,-1],[1,0,0,0,-1,0,0,0,-1],[0,-1,0,-1,0,0,0,0,-1],
  [0,0,-1,0,1,0,1,0,0],[0,0,-1,1,0,0,0,-1,0],[0,0,-1,0,-1,0,-1,0,0],[0,0,-1,-1,0,0,0,1,0],
  [1,0,0,0,0,-1,0,1,0],[0,-1,0,0,0,-1,1,0,0],[-1,0,0,0,0,-1,0,-1,0],[0,1,0,0,0,-1,-1,0,0],
  [-1,0,0,0,0,1,0,1,0],[0,1,0,0,0,1,1,0,0],[1,0,0,0,0,1,0,-1,0],[0,-1,0,0,0,1,-1,0,0]
].map(v => Object.freeze(v)));

export function orientationMatrixForClass(index) {
  const idx = Number(index);
  return Array.isArray(ORIENTATION_MATRICES_24[idx]) ? [...ORIENTATION_MATRICES_24[idx]] : [...ORIENTATION_MATRICES_24[0]];
}

export function nearestOrientationClass(matrixLike) {
  if (!Array.isArray(matrixLike) || matrixLike.length < 9) return 0;
  const input = matrixLike.slice(0, 9).map(Number);
  if (input.some(v => !Number.isFinite(v))) return 0;
  let best = 0;
  let bestDist = Infinity;
  ORIENTATION_MATRICES_24.forEach((candidate, idx) => {
    let d = 0;
    for (let j = 0; j < 9; j += 1) {
      const delta = input[j] - candidate[j];
      d += delta * delta;
    }
    if (d < bestDist) { bestDist = d; best = idx; }
  });
  return best;
}

export function multiplyMatrix3(a, b) {
  const out = new Array(9).fill(0);
  for (let r = 0; r < 3; r += 1) {
    for (let c = 0; c < 3; c += 1) {
      out[r * 3 + c] =
        a[r * 3 + 0] * b[0 * 3 + c] +
        a[r * 3 + 1] * b[1 * 3 + c] +
        a[r * 3 + 2] * b[2 * 3 + c];
    }
  }
  return out;
}

export function transposeMatrix3(m) {
  return [m[0],m[3],m[6], m[1],m[4],m[7], m[2],m[5],m[8]];
}

export function transformVector3(vector, matrixLike) {
  const [x,y,z] = vector.map(Number);
  const m = matrixLike;
  return [
    m[0] * x + m[1] * y + m[2] * z,
    m[3] * x + m[4] * y + m[5] * z,
    m[6] * x + m[7] * y + m[8] * z
  ];
}

export function transformPointByClass(point, classId) {
  return transformVector3(point, orientationMatrixForClass(classId));
}

export function orientedSize(size, classId) {
  const [x,y,z] = size.map(v => Math.abs(Number(v) || 0));
  const m = orientationMatrixForClass(classId).map(Math.abs);
  return [
    m[0] * x + m[1] * y + m[2] * z,
    m[3] * x + m[4] * y + m[5] * z,
    m[6] * x + m[7] * y + m[8] * z
  ];
}

export function composeOrientationClasses(outerClass, innerClass) {
  const outer = orientationMatrixForClass(outerClass);
  const inner = orientationMatrixForClass(innerClass);
  return nearestOrientationClass(multiplyMatrix3(outer, inner));
}

export function canonicalCaseDimensions(worldDims, transformClass) {
  // world = R * canonical, therefore canonical dimensions = |R^T| * world dimensions.
  const rt = transposeMatrix3(orientationMatrixForClass(transformClass)).map(Math.abs);
  const [x,y,z] = worldDims.map(v => Math.abs(Number(v) || 0));
  return [
    rt[0] * x + rt[1] * y + rt[2] * z,
    rt[3] * x + rt[4] * y + rt[5] * z,
    rt[6] * x + rt[7] * y + rt[8] * z
  ];
}

export const TOPOLOGY_TRANSFORMS = Object.freeze({
  standard: 0,
  inverted: 2,
  vertical: 22,
  vertical_inverted: composeOrientationClasses(22, 2)
});
